import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShoppingBag, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { trackBeginCheckout } from '../lib/analytics';
import AddressCard from './checkout/AddressCard';
import { OrderSummaryCard, ShippingMethodCard } from './checkout/OrderSections';
import { PromoCard, PaymentSummaryCard } from './checkout/PaymentSections';
import { useCheckoutShipping, useCheckoutTotals } from './checkout/hooks';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutPage() {
  const { cart, fetchCart } = useCart();
  const { user, loading: authLoading, resendEmailVerification } = useAuth();
  const navigate = useNavigate();

  const [processing, setProcessing] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoResult, setPromoResult] = useState(null);
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [addresses, setAddresses] = useState({ shipping_address: {}, billing_address: {} });
  const [editingAddr, setEditingAddr] = useState(null);
  const [addrForm, setAddrForm] = useState({});
  const [step, setStep] = useState('addresses');
  const [ageAck, setAgeAck] = useState(false);
  const [ruoAck, setRuoAck] = useState(false);
  const [manualMode, setManualMode] = useState(false);

  const {
    shippingOptions, selectedShipping, setSelectedShipping, effectiveOptionRate,
  } = useCheckoutShipping(cart, promoResult);

  const totals = useCheckoutTotals(cart, promoResult, selectedShipping, effectiveOptionRate);

  const fetchAddresses = useCallback(
    () => axios.get(`${API}/user/addresses`, { withCredentials: true })
      .then((r) => setAddresses(r.data))
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.warn('user/addresses fetch failed:', e?.message);
      }),
    [],
  );

  useEffect(() => {
    if (user) { fetchCart(); fetchAddresses(); }
  }, [user, fetchCart, fetchAddresses]);

  // Read the global Manual Checkout Mode flag — public endpoint, no auth.
  useEffect(() => {
    axios.get(`${API}/checkout/mode`)
      .then((r) => setManualMode(!!r.data?.manual))
      .catch(() => setManualMode(false));
  }, []);

  useEffect(() => {
    if (!authLoading && !user) navigate('/login');
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (step === 'payment' && cart?.items?.length) {
      trackBeginCheckout({ value: cart.total, items: cart.items });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handleSaveAddr = async (type) => {
    const required = ['first_name', 'last_name', 'address', 'city', 'state', 'postal_code'];
    if (type === 'billing') required.push('phone', 'email');
    for (const key of required) {
      if (!addrForm[key] || !String(addrForm[key]).trim()) {
        toast.error(`${key.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())} is required`);
        return;
      }
    }
    try {
      await axios.patch(`${API}/user/addresses/${type}`, addrForm, { withCredentials: true });
      toast.success(`${type} address updated`);
      setEditingAddr(null);
      fetchAddresses();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`save ${type} address failed:`, e?.response?.data || e.message);
      toast.error('Failed');
    }
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setApplyingPromo(true);
    try {
      const { data } = await axios.post(`${API}/promo/validate`, { code: promoCode });
      setPromoResult(data);
      if (data.valid) toast.success(`Promo applied: ${data.discount_percent}% off!`);
      else toast.error('Invalid promo code');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('promo/validate failed:', e?.response?.data || e.message);
      toast.error('Failed');
    } finally {
      setApplyingPromo(false);
    }
  };

  const clearPromo = () => { setPromoCode(''); setPromoResult(null); };

  const handleCheckout = async () => {
    if (!ageAck || !ruoAck) {
      toast.error('Please confirm the age (21+) and Research-Use-Only acknowledgments to continue.');
      return;
    }
    setSubmitError(null);
    setProcessing(true);
    try {
      const payload = {
        origin_url: window.location.origin,
        promo_code: promoResult?.valid ? promoCode : null,
        age_acknowledged: true,
        ruo_acknowledged: true,
      };
      if (selectedShipping && totals.discountedSubtotal > 0) {
        payload.shipping_service_code = selectedShipping.service_code;
      }
      const { data } = await axios.post(`${API}/checkout/create-session`, payload, { withCredentials: true });
      if (data.manual && data.url) {
        // Manual Mode: navigate within the SPA to the success page so the
        // user keeps their auth cookie + we don't trigger a full reload.
        const u = new URL(data.url);
        navigate(`${u.pathname}${u.search}`);
        return;
      }
      if (data.url) window.location.href = data.url;
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed';
      const isUnverified = /verify your email/i.test(detail);
      setSubmitError({ message: detail, isUnverified });
      // Inline message with resend link is the better surface for unverified;
      // don't double up with a toast.
      if (!isUnverified) toast.error(detail);
      setProcessing(false);
    }
  };

  const handleResendVerification = async () => {
    setResendingVerification(true);
    const r = await resendEmailVerification();
    setResendingVerification(false);
    if (r.success) {
      setSubmitError({
        message: 'Verification email sent. Check your inbox and click the link, then try checkout again.',
        isUnverified: false, sentOk: true,
      });
      toast.success('Verification email sent');
    } else {
      toast.error(r.error || 'Could not send verification email');
    }
  };

  // Tip-only carts skip the address step entirely (no physical fulfillment).
  // Skip to payment on first render in that case. Must run BEFORE the
  // empty-cart early-return below so React hook order stays stable.
  useEffect(() => {
    if (totals.tipOnly && step === 'addresses') setStep('payment');
  }, [totals.tipOnly, step]);

  if (!cart.items || cart.items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="empty-checkout">
        <ShoppingBag className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
        <h2 className="text-2xl font-heading text-foreground mb-2">Your cart is empty</h2>
        <Button onClick={() => navigate('/catalog')}>Browse Catalog</Button>
      </div>
    );
  }

  const hasShipping = !!addresses.shipping_address?.address;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="checkout-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/catalog')} className="mb-6">
        <ArrowLeft className="h-4 w-4 mr-1" /> Continue Shopping
      </Button>
      <h1 className="text-4xl tracking-tight font-light text-foreground font-heading mb-8">Checkout</h1>

      {step === 'addresses' ? (
        <AddressStep
          addresses={addresses}
          editingAddr={editingAddr}
          addrForm={addrForm}
          setAddrForm={setAddrForm}
          setEditingAddr={setEditingAddr}
          handleSaveAddr={handleSaveAddr}
          userEmail={user?.email}
          hasShipping={hasShipping}
          onContinue={() => setStep('payment')}
        />
      ) : (
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            <Button variant="ghost" size="sm" onClick={() => setStep('addresses')} className="mb-2">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to Addresses
            </Button>
            <OrderSummaryCard items={cart.items} />
            {!totals.tipOnly && (
              <ShippingMethodCard
                discountedSubtotal={totals.discountedSubtotal}
                shippingOptions={shippingOptions}
                selectedShipping={selectedShipping}
                setSelectedShipping={setSelectedShipping}
                effectiveOptionRate={effectiveOptionRate}
              />
            )}
          </div>
          <div className="space-y-4">
            <PromoCard
              promoCode={promoCode} setPromoCode={setPromoCode}
              promoResult={promoResult}
              applying={applyingPromo}
              onApply={handleApplyPromo}
              onClear={clearPromo}
            />
            <PaymentSummaryCard
              cart={cart} totals={totals}
              ageAck={ageAck} setAgeAck={setAgeAck}
              ruoAck={ruoAck} setRuoAck={setRuoAck}
              processing={processing} onCheckout={handleCheckout}
              submitError={submitError}
              onResendVerification={handleResendVerification}
              resendingVerification={resendingVerification}
              manualMode={manualMode}
            />
          </div>
        </div>
      )}
    </div>
  );
}


function AddressStep({
  addresses, editingAddr, addrForm, setAddrForm, setEditingAddr,
  handleSaveAddr, userEmail, hasShipping, onContinue,
}) {
  const beginEdit = (type) => {
    setEditingAddr(type);
    setAddrForm(addresses[`${type}_address`] || {});
  };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-heading font-medium">Confirm Your Addresses</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <AddressCard
          type="shipping" label="Shipping Address"
          address={addresses.shipping_address}
          isEditing={editingAddr === 'shipping'}
          addrForm={addrForm} setAddrForm={setAddrForm}
          onEdit={() => beginEdit('shipping')}
          onCancel={() => setEditingAddr(null)}
          onSave={() => handleSaveAddr('shipping')}
          userEmail={userEmail}
        />
        <AddressCard
          type="billing" label="Billing Address"
          address={addresses.billing_address}
          isEditing={editingAddr === 'billing'}
          addrForm={addrForm} setAddrForm={setAddrForm}
          onEdit={() => beginEdit('billing')}
          onCancel={() => setEditingAddr(null)}
          onSave={() => handleSaveAddr('billing')}
          userEmail={userEmail}
          shippingForCopy={addresses.shipping_address}
        />
      </div>
      <Button
        className="w-full sm:w-auto bg-[hsl(210,45%,40%)] hover:bg-[hsl(210,45%,30%)] text-white"
        onClick={onContinue}
        disabled={!hasShipping}
        data-testid="proceed-to-payment"
      >
        {hasShipping ? 'Confirm and Continue' : 'Add shipping address to continue'}
      </Button>
    </div>
  );
}
